
import { Vue, Component } from 'vue-property-decorator'
import config from '@/config'
import VueQr from 'vue-qr'
import { useClientStore, ClientType } from '@/store/client'
import { useReuseTabStore } from '@/store/reuse-tab'
import { useMenuStore } from '@/store/menu'
import { useUserStore } from '@/store/user'
import { useMixAuthStore, ModuleType } from '@/store/mix-auth'
import { useTokenStore } from '@/store/token'
import pinia from '@/store'

import { GlobalActions } from '../../../../../enums/globa'

@Component({
  name: 'Workbench',
  components: {
    VueQr,
  },
})
export default class Workbench extends Vue {
  private clientStore = useClientStore(pinia)
  private reuseTabStore = useReuseTabStore(pinia)
  private menuStore = useMenuStore(pinia)
  private mixAuthStore = useMixAuthStore(pinia)
  private userStore = useUserStore(pinia)
  private tokenStore = useTokenStore(pinia)

  private get isAdmin() {
    return ['admin', 'kcsupperadmin'].includes(this.mixAuthStore.getUserName)
  }

  private get entryList() {
    const allEntry: Array<{
      key: number
      title: string
      icon: string
      textColor: string
      backgroundStyle: string
      isHidden?: boolean
      isBlur?: boolean
      onClick?: () => void
      onBlurBtn?: () => void
    }> = [
      {
        key: ModuleType.ExpertLibrary,
        title: '建设工程勘察、设计、消防技术专家库',
        icon: require('@/assets/images/workbench/1728@2x.webp'),
        textColor: '#355049',
        backgroundStyle: 'linear-gradient( 180deg, #E1F5EE 0%, #FAFFFD 100%)',
        isBlur: !this.mixAuthStore.hasModule(ModuleType.ExpertLibrary),
        onClick: this.toExpertLibraryPage,
        onBlurBtn: () => {
          this.$confirm({
            title: '提示',
            content: '确定注册为专家库专家？',
            onOk: () => this.toRegisterExpert(),
          })
        },
      },
      {
        key: ModuleType.SurveyDrawing,
        title: '广西工程勘察设计行业监管平台',
        icon: require('@/assets/images/workbench/1730@2x.webp'),
        textColor: '#524736',
        backgroundStyle: 'linear-gradient(180deg, #FFF1E0 0%, #FFFDFA 100%)',
        isBlur: !this.mixAuthStore.hasModule(ModuleType.SurveyDrawing),
        onClick: this.toSurveyDrawingSystem,
        onBlurBtn: () => {
          this.$confirm({
            title: '提示',
            width: 600,
            content: (h) => [
              h(
                'p',
                { style: 'margin-top: 16px; font-size: 15px' },
                '确定注册广西工程勘察设计行业监管平台账户并开展业务？',
              ),
              h('a-alert', {
                props: {
                  message:
                    '广西壮族自治区住房和城乡建设主管部门人员如要开通广西工程勘察设计行业监管平台权限，请咨询系统客服获取开通权限操作说明；非主管部门人员，请点击下方“确定”完善信息即可。',
                  type: 'info',
                },
              }),
            ],
            onOk: () => this.toSurveyDrawingSystem(),
          })
        },
      },
      {
        key: ModuleType.FireDesignReview,
        title: '广西建设工程消防设计审查验收备案管理平台',
        icon: require('@/assets/images/workbench/1729@2x.webp'),
        textColor: '#3A4761',
        backgroundStyle: 'linear-gradient(180deg, #E0F6FF 0%, #F5FCFF 100%)',
        isBlur: !this.mixAuthStore.hasModule(ModuleType.FireDesignReview),
        onClick: this.toFireDesignReview,
        onBlurBtn: () => {
          this.$confirm({
            title: '提示',
            width: 600,
            content: (h) => [
              h('p', { style: 'margin-top: 16px; font-size: 15px' }, '确定注册为建设单位并开展消防审验业务？'),
              h('a-alert', {
                props: {
                  message:
                    '广西壮族自治区住房和城乡建设主管部门人员如要开通广西建设工程消防设计审查验收备案管理平台权限，请咨询系统客服获取开通权限操作说明；非主管部门人员，请点击下方“确定”即可。',
                  type: 'info',
                },
              }),
            ],
            onOk: () => this.toFireDesignReview(),
          })
        },
      },
      {
        key: ModuleType.ElectricBicycle,
        title: '电动自行车安全隐患全链条治理平台',
        icon: require('@/assets/images/workbench/1731@2x.webp'),
        textColor: '#52363A',
        backgroundStyle: 'linear-gradient( 180deg, #FCE8F3 0%, #FFFAFD 100%)',
        isHidden: !this.mixAuthStore.hasModule(ModuleType.ElectricBicycle),
        onClick: this.toElectricBicycleManagePage,
      },
    ]

    return allEntry.filter((item) => (typeof item.isHidden === 'boolean' ? !item.isHidden : true))
  }

  private columns = [
    {
      title: '操作',
      scopedSlots: { customRender: 'action' },
    },
    {
      title: '事项名称',
      dataIndex: 'name',
    },
    {
      title: '流程类型',
      dataIndex: 'type',
    },
    {
      title: '节点到达时间',
      dataIndex: 'time',
    },
  ]
  private dataSource = []

  private specialRectificationQRCode = location.origin + '/sign/special-rectification/login'
  private electricBicycleQRCode = location.origin + '/sign/phone-login'

  private loadingStatus: ModuleType[] = []
  private openLoading(key: ModuleType) {
    if (this.loadingStatus.includes(key)) return
    this.loadingStatus.push(key)
  }
  private closeLoading(key: ModuleType) {
    this.loadingStatus = this.loadingStatus.filter((item) => item !== key)
  }

  private mounted() {
    this.closeFullScreenLoading()
  }

  private closeFullScreenLoading() {
    const loading = document.getElementById('main-loading')
    loading?.remove()
  }

  private toSurveyDrawingSystem() {
    this.openLoading(ModuleType.SurveyDrawing)
    this.mixAuthStore
      .getLoginCode()
      .then((code) => {
        const surveyLink = (config.get('GX_SURVEY_HREF') || '').endsWith('/')
          ? config.get('GX_SURVEY_HREF').slice(0, -1)
          : config.get('GX_SURVEY_HREF')
        this.closeLoading(ModuleType.SurveyDrawing)
        window.open(`${surveyLink}/mixauthsso?code=${code}`, '_blank')
      })
      .catch((err) => {
        this.closeLoading(ModuleType.SurveyDrawing)
        this.$message.error(err.message || '授权获取失败')
      })
  }

  private async toFireDesignReview() {
    try {
      this.openLoading(ModuleType.FireDesignReview)
      const isAuthSuccess = await this.loginFireAuth()
      if (!isAuthSuccess) return this.closeLoading(ModuleType.FireDesignReview) // 授权失败中断

      this.mixAuthStore.addModule(ModuleType.FireDesignReview)
      this.$nextTick(async () => {
        await this.clientStore.changeToClient(ClientType.Web)
        this.closeLoading(ModuleType.FireDesignReview)
        window.open('/fire/home', '_blank')
      })
    } catch (error) {
      this.closeLoading(ModuleType.FireDesignReview)
    }
  }

  private async toExpertLibraryPage() {
    try {
      this.openLoading(ModuleType.ExpertLibrary)
      const isAuthSuccess = await this.loginFireAuth()
      if (!isAuthSuccess) return this.closeLoading(ModuleType.ExpertLibrary) // 授权失败中断

      const findFirstLeafNode = (tree, predicate) => {
        for (let node of tree) {
          if (predicate(node) && (!node.children || node.children.length === 0)) return node
          if (node.children && node.children.length > 0) {
            const result = findFirstLeafNode(node.children, predicate)
            if (result) return result
          }
        }
        return null
      }

      const menus = this.menuStore.menu
      const expertMenuNode = findFirstLeafNode(menus, (item) => item.link.startsWith('/expert/'))

      // 跳转至专家库第一个子菜单链接
      this.$nextTick(async () => {
        this.closeLoading(ModuleType.ExpertLibrary)
        await this.clientStore.changeToClient(ClientType.Web)
        if (expertMenuNode && expertMenuNode.link) {
          window.open(expertMenuNode.link, '_blank')
        } else {
          window.open('/fire/home', '_blank')
        }
      })
    } catch (error) {
      this.closeLoading(ModuleType.ExpertLibrary)
    }
  }

  private async toRegisterExpert() {
    try {
      this.openLoading(ModuleType.ExpertLibrary)
      const userInfo = await this.mixAuthStore.getUserInfo()
      if (!userInfo) return

      this.$expertHttp
        .post('/expert/User/RegisterPersonnel', {
          userName: userInfo.userName,
          realName: userInfo.realName,
          phone: userInfo.phoneNumber,
          idCard: userInfo.idCard,
          rhId: userInfo.userId,
          enterpriseName: userInfo.enterprise?.name || '',
          ucss: userInfo.enterprise?.organizationCode || '',
        })
        .then(async (res) => {
          this.mixAuthStore.addModule(ModuleType.ExpertLibrary)
          this.toExpertLibraryPage()
        })
        .catch((err) => {
          this.$message.error(err.message || '注册专家失败')
          this.closeLoading(ModuleType.ExpertLibrary)
        })
    } catch (err) {
      this.$message.error(err.message || '授权获取失败')
      this.closeLoading(ModuleType.ExpertLibrary)
    }
  }

  private async toElectricBicycleManagePage() {
    try {
      if (this.mixAuthStore.hasModule(ModuleType.ElectricBicycle)) {
        this.openLoading(ModuleType.ElectricBicycle)
        const isAuthSuccess = await this.loginFireAuth()
        if (!isAuthSuccess) return this.closeLoading(ModuleType.ElectricBicycle) // 授权失败中断

        this.$nextTick(async () => {
          if (this.clientStore.clients.includes(ClientType.ElectricVehicleInspection)) {
            await this.clientStore.changeToClient(ClientType.ElectricVehicleInspection)
            // this.reuseTabStore.clear()
            this.closeLoading(ModuleType.ElectricBicycle)
            window.open('/fire/home', '_blank')
          }
        })
      }
    } catch (error) {
      this.closeLoading(ModuleType.ElectricBicycle)
    }
  }

  /**
   * 登录消防系统（设置消防Token、菜单、按钮等权限）
   */
  private loginFireAuth() {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.tokenStore.getToken()) {
          const initRes = await this.userStore.init()
          const enterpriseType = (initRes as any).data.userInfo?.enterpriseType || ''
          if (enterpriseType.includes('主管单位')) {
            const showTips = await this.showOrganizerAccountExamTips(this.tokenStore.getToken())
            if (showTips) return resolve(false)
          }

          // 已经登录的直接初始化更新数据
          await this.userStore.logged()
          if (window.__POWERED_BY_QIANKUN__) {
            this.$setGlobalState({
              action: GlobalActions.SignIn,
              userInfo: this.userStore.info,
              token: this.tokenStore.getToken(),
            })
            return resolve(true)
          } else {
            return resolve(true)
          }
        } else {
          // 未登录的先获取登录，再更新数据
          this.$http
            .get('/api/AuthSSO/Login', {
              code: await this.mixAuthStore.getLoginCode(),
            })
            .then(async (res) => {
              const { access_token } = (res as any).data.loginResult
              const enterpriseType = (res as any).data.loginResult?.user?.enterpriseType || ''
              if (enterpriseType.includes('主管单位')) {
                const showTips = await this.showOrganizerAccountExamTips(access_token)
                if (showTips) return resolve(false)
              }

              this.tokenStore.setToken(String(access_token || ''))
              await this.userStore.logged()
              if (window.__POWERED_BY_QIANKUN__) {
                this.$setGlobalState({
                  action: GlobalActions.SignIn,
                  userInfo: this.userStore.info,
                  token: access_token,
                })
                return resolve(true)
              } else {
                return resolve(true)
              }
            })
            .catch((err) => {
              this.$message.error(err.message || '获取授权失败！')
              reject(err)
            })
        }
      } catch (err) {
        reject(err)
      }
    })
  }

  public toHall() {
    this.$router.push({
      path: '/hall',
    })
  }

  private async toSpecialRectificationList() {
    if (!this.mixAuthStore.hasModule(ModuleType.SpecialRectification)) return

    const findTreeNode = <T extends { children?: T[] }>(treeData: T[], predicate: (node: T) => boolean): T | null => {
      for (const node of treeData) {
        if (predicate(node)) {
          return node
        }
        if (Array.isArray(node.children)) {
          const result = findTreeNode(node.children, predicate)
          if (result) {
            return result
          }
        }
      }
      return null
    }

    const isAuthSuccess = await this.loginFireAuth()
    if (!isAuthSuccess) return // 授权失败中断

    const routeLink = '/fire/special-rectification/list'
    const hasSpecialRectificationListPage = !!findTreeNode(this.menuStore.menu, (node) => {
      return node.link === routeLink
    })
    if (hasSpecialRectificationListPage) {
      this.$nextTick(() => {
        window.open(routeLink, '_blank')
      })
    }
  }

  /**
   * 检查是否有必须的考试
   */
  private checkRequiredExamination(accessToken: string) {
    return this.$examHttp
      .get('/api/ksexamination/testskillexamination', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Qwert: '6yyrYfcPi28LuzpH',
        },
      })
      .then((res) => !!(res as any)?.data)
  }

  /**
   * 显示主管单位账号考试提示
   */
  private async showOrganizerAccountExamTips(accessToken) {
    const isRequiredExam = await this.checkRequiredExamination(accessToken)
    if (isRequiredExam) {
      this.$info({
        title: '提示',
        content: (h) =>
          h(
            'p',
            { style: { fontSize: '16px' } },
            '新增的主管单位账号，需下载“桂建消防”App并完成考试，考试通过即可办理业务。',
          ),
      })
    }
    return isRequiredExam
  }
}
